<template>
    <framework>
        <template #side>
            <div class="logo">
                <img v-if="enrollment.course.organization.logo" :src="apiUrl + 'app/organizations/logo/' + enrollment.course.organization.logo"/>
            </div>
            <!--Pre-Components-->
            <div v-if="!preComplete && !enrollment.completed_at">
                <b-message type="is-success" size="is-small">
                    <div>
                        There <span v-if="preSteps !== 1">are</span><span v-else>is</span> {{ preSteps }}
                        step<span v-if="preSteps !== 1">s</span> required before you start your course.
                        <strong>Complete the steps below to continue.</strong></div>
                </b-message>

                <template v-for="config in enrollment.configs">
                    <div class="subheader">{{ config.title }}</div>
                    <div v-for="o in config.pre_components">
                        :icon="(o.completed_at) ? (!o.approved_at) ? 'fa-pause-circle' : 'fa-check-circle' : 'fa-circle'"
                        :title="o.title"
                        :subtitle="(o.completed_at && !o.approved_at) ? 'Awaiting approval...' : null"
                        @click="$openCard('view-form', {componentId: o.component_id, enrollmentId: props.enrollmentId, configId: config.course_config_id, type: 'pre'}, card)"
                        :active="child && child.definition.component === 'view-form' && child.props.componentId === o.component_id.toString()"
                    </div>
                </template>
            </div>

            <!--Main Outline-->
            <div class="menu" v-else-if="preComplete || (enrollment.completed_at && postComplete)">
                <ul>
                    <template v-for="o in enrollment.course.outline">
                        <li class="sub-header" v-if="o.type === 'HEADER'">{{ o.title }}</li>

                        <li
                            v-if="o.type === 'COMPONENT' && o.closed && !o.completed_at"
                            @click="contentAlert"
                        >
                            <div class="columns is-gapless">
                                <div class="column is-narrow">
                                    <i class="far fa-fw fa-times-circle" :style="{'color': colors.primary}"></i>
                                </div>
                                <div class="column">{{ o.title }}</div>
                            </div>
                        </li>
                        <router-link
                            v-else
                            :to="{name: 'legacy-component', params: {outlineId: o.id}}"
                            custom v-slot="{ navigate, isActive }"
                            active-class="active"
                        >
                            <li
                                @click="navigate"
                                :class="{'active': isActive}"
                                :style="{'background-color': (isActive) ? colors.primary + ' !important' : 'transparent'}"
                                v-if="o.type === 'COMPONENT'"
                            >
                                <div class="columns is-gapless">
                                    <div class="column is-narrow">
                                        <i
                                            class="far fa-fw"
                                            :class="(o.completed_at) ? 'fa-check-circle' : (o.closed) ? 'fa-times-circle' : 'fa-circle'"
                                            :style="{'color': colors.primary}"
                                        ></i>
                                    </div>
                                    <div class="column">{{ o.title }}</div>
                                </div>
                            </li>
                        </router-link>
                    </template>
                </ul>

            </div>

            <!-- Post Components-->
            <div v-if="preComplete && enrollment.completed_at && !postComplete">
                <b-message type="is-success" size="is-small">
                    <div>
                        There <span v-if="postSteps !== 1">are</span><span v-else>is</span> {{ postSteps }}
                        step<span v-if="postSteps !== 1">s</span> required to get full credit for this course.
                        <strong>Complete the steps below to continue.</strong></div>
                </b-message>

                <template v-for="config in enrollment.configs">
                    <div class="subheader">{{ config.title }}</div>
                    <div v-for="o in config.post_components">

                        <i class="far fa-fw" :class="(o.completed_at) ? (!o.approved_at) ? 'fa-pause-circle' : 'fa-check-circle' : 'fa-circle'"></i>
                        {{ o.title }}
                        :subtitle="(o.completed_at && !o.approved_at) ? 'Awaiting approval...' : null"
                    </div>
                </template>
            </div>

            <!--Course Complete-->
            <div v-if="preComplete && enrollment.completed_at && postComplete">
                <template v-for="config in enrollment.configs">
                    <div class="subtitle">{{ config.title }}</div>
                    <div class="task-item" v-for="o in config.reports">
                        <i class="fas fa-file-pdf"></i>{{ o.title }}
                    </div>
                </template>
            </div>

        </template>
        <template #main>
            <div v-if="enrollment.course.id">
                <course-header :title="enrollment.course.title" @close="closeCourse"/>
                <!-- NOTE: the "complete course" button was removed — completed_at is
                     set automatically by the server-side completion predicate -->
                <div v-if="!enrollment.completed_at && courseComplete && !outlineId">
                    <div class="columns">
                        <div class="column is-9">
                            <ui-card class="mt-5">
                                <div class="columns">
                                    <div class="column is-narrow">
                                        <div class="pl-2">
                                            <i class="far fa-check-circle fa-3x"></i>
                                        </div>
                                    </div>
                                    <div class="column">
                                        <div class="mb-3 is-size-4">You've finished all the requirements of this course.</div>
                                        <div class="mb-5">You can review the content by selecting a topic.</div>
                                    </div>
                                    <div class="column is-narrow"></div>
                                </div>
                            </ui-card>

                        </div>
                    </div>
                </div>
                <div class="course-frame">
                    <router-view @update="loadEnrollment()"/>
                </div>
            </div>
            <div class="content mt-6" v-else>
                <h3 class="has-text-white"><i class="fas fa-spinner fa-spin mr-4"></i>Please wait. Loading content...</h3>
            </div>
        </template>
    </framework>
</template>

<script>
    import Framework from "../../components/framework/Framework";
    import MainHeader from "../../components/framework/MainHeader";

    import {mapGetters, mapMutations} from 'vuex';

    import {client as http, apiUrl} from "../../http_client";
    import CourseHeader from "../../components/course/CourseHeader";
    import moment from "moment";
    import UiCard from "../../components/UiCard";

    export default {
        components: {UiCard, CourseHeader, Framework, MainHeader},
        props: ['enrollmentId', 'outlineId'],
        data: function() {
            return {
                enrollment: {
                    configs: [],
                    course: {
                        outline: [],
                        organization: {}
                    },
                    timer: null,
                    time: 0,
                    uuid: null,
                    timeout: 60
                },
                apiUrl
            };
        },
        computed: {
            ...mapGetters(['colors']),
            preSteps: function() {
                let steps = 0;
                if (!this.enrollment.configs) return 0;
                for (let i = 0; i < this.enrollment.configs.length; i++) {
                    const config = this.enrollment.configs[i];
                    for (let j = 0; j < config.pre_components.length; j++) {
                        const component = config.pre_components[j];
                        if (component.completed_at === null) steps++;
                    }
                }
                return steps;
            },
            preComplete: function() {
                for (let y = 0; y < this.enrollment.configs.length; y++) {
                    const config = this.enrollment.configs[y];

                    for (let i = 0; i < config.pre_components.length; i++) {
                        if (config.pre_components[i].completed_at === null || config.pre_components[i].approved_at === null) return false;
                    }
                }
                return true;
            },
            postSteps: function() {
                let steps = 0;
                if (!this.enrollment.configs) return 0;
                for (let i = 0; i < this.enrollment.configs.length; i++) {
                    const config = this.enrollment.configs[i];
                    for (let j = 0; j < config.post_components.length; j++) {
                        const component = config.post_components[j];
                        if (component.completed_at === null) steps++;
                    }
                }
                return steps;
            },
            postComplete: function() {
                for (let y = 0; y < this.enrollment.configs.length; y++) {
                    const config = this.enrollment.configs[y];

                    for (let i = 0; i < config.post_components.length; i++) {
                        if (config.post_components[i].completed_at === null || config.post_components[i].approved_at === null) return false;
                    }
                }
                return true;
            },
            courseComplete: function() {
                if (!this.enrollment.course.outline) return false;
                for (let i = 0; i < this.enrollment.course.outline.length; i++) {
                    if (this.enrollment.course.outline[i].completed_at === null) return false;
                }
                return true;
            },
            formattedTime: function() {
                return moment.utc(this.time * 1000).format('HH:mm:ss');
            },
        },
        methods: {
            ...mapMutations(['setColors']),
            loadEnrollment: function() {
                http.get('app/enrollments/' + this.enrollmentId, {force: true}).then(response => {
                    this.enrollment = response.data;

                    let closed = null;
                    for (let i = 0; i < this.enrollment.course.outline.length; i++) {
                        const o = this.enrollment.course.outline[i];
                        if (o.type !== 'HEADER') {
                            if (o.completed_at) o.closed = true;

                            if (closed === null && !o.closed) {
                                o.closed = false;
                                closed = true;
                            } else {
                                o.closed = true;
                            }

                            if (!o.completed_at && !o.closed && !this.outlineId) {
                                this.$router.push({name: 'legacy-component', params: {outlineId: o.id}});
                            }
                        }
                    }

                    this.setColors({
                        primary: this.enrollment.course.organization.primary_color,
                        background1: this.enrollment.course.organization.background_color_1,
                        background2: this.enrollment.course.organization.background_color_2,
                    });
                }).catch(err => {
                    if (err) console.log(err);
                });
            },
            contentAlert: function() {
                window.alert('This content is not available until you complete previous content.');
            },
            closeCourse: function() {
                this.$router.push({name: 'enrollments'});
            }
        },
        mounted() {
            this.loadEnrollment();
        },
        destroyed() {
            this.setColors({
                primary: '#336699',
                background1: '#336699',
                background2: '#213f6a',
            });
        }
    };
</script>

<style lang="scss" scoped>
    .course-frame {
        top: 100px;
        left: 5rem;
        right: 4rem;
        bottom: 1rem;
    }

    .logo {
        padding: 1.3rem 1.4rem 0;
    }

    .menu {
        position: absolute;
        top: 110px;
        bottom: 0;
        left: 0;
        right: 0;
        overflow-y: auto;

        background-color: #fff;
        background-image: linear-gradient(white, #eeeeee);

        h1 {
            font-weight: 400;
            font-size: 30px;
            margin-top: 1rem;
            display: inline-block;
            cursor: pointer;
            padding: 1rem 1.5rem 0;
            color: #4a4a4a;
            letter-spacing: -1px;

            span {
                font-weight: 900;
                margin-right: 3px;

                color: #336699;
            }
        }

        ul {
            margin-top: 1.1rem;

            li {
                border-bottom: 1px solid #e0e2e8;
                font-size: 0.9rem;
                padding: 0.8rem 1.4rem 0.8rem 1rem;
                color: #4a4a4a;
                transition: all 200ms;
                font-weight: bold;
                cursor: pointer;
                background-color: rgba(255, 255, 255, 0.5);

                &.active {
                    background-color: #336699 !important;

                    color: white;

                    .meta, i {
                        color: white !important;
                    }
                }

                &.sub-header {
                    font-weight: bold !important;
                    cursor: default !important;
                    color: #4a4a4a !important;
                    padding: 0.5rem 1.2rem 0.3rem !important;
                    font-size: 0.8rem !important;
                    text-transform: uppercase;
                    background-color: #f3f3f5 !important;
                }

                &:hover {
                    background-color: #919191 !important;
                    color: white;

                    .meta, i {
                        color: white !important;
                    }
                }

                &:first-child {
                    border-top: 1px dashed #e0e2e8;
                }

                i {
                    color: #3c6691;
                    font-size: 1.2rem;
                    vertical-align: middle;
                    transition: all 200ms;
                    margin-right: 0.9rem
                }

                .meta {
                    transition: color 200ms;

                    font-weight: normal;
                    font-size: 0.8rem;
                    padding-left: 3.15rem;
                    color: #737881;
                    line-height: 1rem;
                }
            }

            &.hide-details {
                li {
                    font-size: 0.9rem;
                    font-weight: normal;
                    padding: 0.8rem 1.4rem;

                    .meta {
                        display: none;
                    }

                    i {
                        font-size: 1.2rem;
                        margin-right: 0.8rem;
                    }
                }
            }
        }

        &.minimized {
            overflow: hidden;

            h1 {
                display: none;
            }

            ul {
                padding-top: 4.7rem;

                li {
                    padding: 1rem 1.1rem;

                    i {
                        font-size: 1.3rem;
                    }

                    .nav-title, .meta {
                        display: none;
                    }

                    &.sub-header {
                        display: none;
                    }
                }
            }
        }
    }

    .controls {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        border-top: 1px solid #ccc;
        background-color: white;
        padding: 1.5rem 1rem;
        overflow: hidden;

        i {
            font-size: 1.2rem;
            vertical-align: middle;
        }
    }
</style>
